import 'whatwg-fetch';

import { message, Modal } from 'antd';
import * as Cookies from 'js-cookie';
import { identity, includes, isEmpty, pickBy } from 'lodash';
import * as parser from 'query-string';

import { HTTPStatusCode } from '../constants/CONSTANTS';
import { translation } from '../store';
import globalContext from './global-context';

const defaultHeaders = {
  'Content-Type': 'application/json',
};

interface IFetchAPIOptions extends RequestInit {
  auth?: boolean; // if true, auth token is put into Headers, else it's put into query string.
  payload?: {};
  token?: string;
  version?: string;
  is_json?: boolean;
  is_public?: boolean;
  force_full_endpoint?: boolean;
  refreshed?: boolean;
  refresh_on_unauthorized?: boolean;
  hide_default_error?: boolean;
}
export class HTTPError extends Error {
  code?: string;
  errorBody?: string;
  errorId?: string;
  constructor(
    errorMessage: string,
    errorCode: string,
    errorBody?: string,
    errorId?: string
  ) {
    super(errorMessage);
    this.code = errorCode;
    this.errorBody = errorBody;
    this.errorId = errorId;
  }
}

const downloadableContentTypes = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'application/zip',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const getToken = (): string | string[] | undefined => {
  return Cookies.get('access_token_cookie');
};

export const refreshAndGetToken = async () => {
  if (window.hasOwnProperty('localStorage')) {
    const item = JSON.parse(
      window.localStorage.getItem('persist:root') || '{}'
    );
    if (item && item.user != null && item.user !== 'null') {
      const user = JSON.parse(item.user);
      if (user.refresh) {
        const refreshResponse = await window.fetch(
          `${process.env.REACT_APP_API_URL}/api/v1.0/auth/refresh`,
          {
            headers: new Headers({
              Authorization: `Bearer ${user.refresh}`,
            }),
            method: 'POST',
          }
        );
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          const newUser = { ...user, token: refreshData.token };
          const newItem = JSON.parse(
            window.localStorage.getItem('persist:root') || '{}'
          );
          newItem.user = JSON.stringify(newUser);
          localStorage.setItem('persist:root', JSON.stringify(newItem));
        }
      }
    }
  }
};

export const getLang = (): string | string[] | undefined => {
  const lang = parser.parse(window.location.search).lang;
  return lang || 'en';
};

export const fetchAPI = async (
  endpoint: string,
  options?: IFetchAPIOptions
): Promise<any> => {
  const {
    payload = {},
    version = '1.0',
    method = 'GET',
    is_json = true,
    force_full_endpoint = false,
    is_public = false,
    ...fetchOptions
  }: IFetchAPIOptions = options || {};

  if (
    endpoint === 'auth/refresh' &&
    window.localStorage.getItem('authPopup') === '1'
  ) {
    return;
  }
  let body;

  let headers = {};
  let qs = {};
  const lang = getLang();

  if (lang) {
    headers = { ...headers, 'Accept-Language': lang };
  }

  if (is_json) {
    headers = { ...headers, ...defaultHeaders };
  }
  if (endpoint === 'auth/refresh') {
    const CRSFRefreshToken = Cookies.get('csrf_refresh_token');
    if (CRSFRefreshToken) {
      headers = {
        ...headers,
        'X-CSRF-TOKEN': CRSFRefreshToken,
      };
    }
  } else {
    const CRSFAccessToken = Cookies.get('csrf_access_token');
    const saToken = localStorage.getItem('sa-token');
    if (CRSFAccessToken) {
      headers = { ...headers, 'X-CSRF-TOKEN': CRSFAccessToken };
    }

    if (saToken) {
      headers = {
        ...headers,
        Authorization: `Bearer ${saToken}`,
      };
    }
    if (is_public) {
      const saPublicToken = localStorage.getItem('sa-public-token');
      const saPublicEmail = localStorage.getItem('sa-public-email');
      headers = {
        ...headers,
        'X-SA-PUBLIC-TOKEN': saPublicToken,
        'X-SA-PUBLIC-EMAIL': saPublicEmail,
      };
    }
    headers = { ...headers, ...fetchOptions.headers };
  }
  if (method === 'GET') {
    qs = parser.stringify(pickBy({ ...qs, ...payload }, identity));
  } else {
    if (fetchOptions.body) {
      body = fetchOptions.body;
    } else {
      // for safari, you can't pass empty {} to body ...
      body = Object.keys(payload).length ? JSON.stringify(payload) : undefined;
    }
  }

  let url = `${process.env.REACT_APP_API_URL}/api/v${version}/${endpoint}${
    !isEmpty(qs) ? `?${qs}` : ''
  }`;

  if (force_full_endpoint) {
    url = endpoint;
  }

  const fetchInit: RequestInit = {
    body,
    headers: new Headers(headers),
    method,
    credentials: 'include',
  };
  if (fetchOptions && Object.keys(fetchOptions).length > 0) {
    Object.assign(fetchInit, fetchOptions);
  }
  const response = await window.fetch(url, fetchInit);
  if (!response.ok) {
    const json = await response.json();
    const responseTranslations = translation()?.response_code || {};
    const fallbackMessage =
      json.error_message ||
      responseTranslations[json.error_code] ||
      'Something went wrong. Please try again later.';

    if (json.error_code === HTTPStatusCode.SERVER_ERROR) {
      message.error(
        responseTranslations.SERVER_ERROR ||
          'The server has encountered an unexpected error.'
      );
    } else if (json.error_code === HTTPStatusCode.UNPROCESSABLE) {
      // business-rule rejection is rendered by the caller (e.g. RFI ticket
      // form opens a Modal with the blocking question & answer), so skip the
      // generic toast here to avoid duplicate/confusing messages
      const isBusinessRuleError =
        json.error_body?.error_type === 'BUSINESS_RULE_PREVENT_TICKET_CREATION';

      if (!isBusinessRuleError) {
        message.error(
          responseTranslations.UNPROCESSABLE ||
            fallbackMessage ||
            'Invalid parameters. Please check your input again.'
        );
      }
    } else if (json.error_code === HTTPStatusCode.TOO_MANY_REQUEST) {
      message.error('Too many attempts. Please wait and try again later.');
    } else if (json.error_code === HTTPStatusCode.UNAUTHORIZED) {
      if (!options?.refreshed) {
        const CRSFRefreshToken = Cookies.get('csrf_refresh_token');
        if (CRSFRefreshToken) {
          headers = {
            ...headers,
            'X-CSRF-TOKEN': CRSFRefreshToken,
          };
        }

        const refreshResponse = await window.fetch(
          `${process.env.REACT_APP_API_URL}/api/v${version}/auth/refresh`,
          {
            headers: new Headers({
              ...headers,
            }),
            method: 'POST',
            credentials: 'include',
          }
        );
        if (refreshResponse.ok) {
          return await fetchAPI(endpoint, { ...options, refreshed: true });
        } else {
          showSessionTimeOutModal();
        }
      } else {
        showSessionTimeOutModal();
      }

      if (!window.location.pathname.startsWith('/login')) {
        showSessionTimeOutModal();
      }
    }

    throw new HTTPError(
      json.error_message,
      json.error_code,
      json.error_body,
      json.error_id
    );
  }
  const contentType = response.headers.get('Content-Type');
  if (includes(downloadableContentTypes, contentType)) {
    const blob = await response.blob();
    return blob;
  }

  let ret;
  try {
    ret = await response.json();
  } catch (e) {
    ret = await response.statusText;
  }

  return ret;
};
export function getUrlParams() {
  return parser.parse(window.location.search);
}

export function getFullAPIURLWithEndpoint(endpoint: string, version: string) {
  return `${process.env.REACT_APP_API_URL}/api/${version}/${endpoint}`;
}

export function getDbsUploadUrl() {
  const url = process.env.REACT_APP_API_URL || '';
  if (url.includes('api-sa-prod') || url.includes('api-dhl-dev')) {
    return 'https://sos-apis.sourcesage.co/dbs/api/v1.0/upload';
  }
  return 'https://staging-apis.sourcesage.co/dbs/api/v1.0/upload';
}

export const downloadFile = (blob: Blob, filename: string = 'download.csv') => {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  if (document.body) {
    document.body.appendChild(link);
    link.click();
  }
};

function showSessionTimeOutModal() {
  if (!globalContext.timeOutModal) {
    globalContext.timeOutModal = true;
    Modal.warning({
      title: translation()?.form?.session_expired || 'Session Expired',
      content:
        translation()?.form?.session_expired_desc ||
        'Your session has expired. Please try to login again',
      onOk: () => {
        window.localStorage.clear();
        setTimeout(() => {
          try {
            window.localStorage.clear();
            Object.keys(Cookies.get()).forEach(cookieName => {
              Cookies.remove(cookieName);
            });
          } catch (error) {}
        });
        window.location.href =
          '/login?redirect=' +
          encodeURIComponent(window.location.pathname + window.location.search);
      },
    });
  }
}
